import { useState, useEffect, useCallback } from 'react';
import { profileApi, BackendApiError } from '../services/backendApi';
import { ProfileFormData, ManagerProfileFormData, ProfileState } from '../types';
import { useAuth } from './useAuth';

export const useProfile = () => {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<ProfileState>({
    profile: null,
    loading: true,
    error: null,
  });

  const fetchProfile = useCallback(async () => {
    if (!isAuthenticated) return;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const profile = await profileApi.getMe();
      setState({ profile, loading: false, error: null });
    } catch (err) {
      if (err instanceof BackendApiError && err.status === 404) {
        setState({ profile: null, loading: false, error: null });
        return;
      }
      setState(prev => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : 'Failed to load profile',
      }));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const updateProfile = useCallback(async (formData: ProfileFormData) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const profile = await profileApi.updateMe(formData);
      setState({ profile, loading: false, error: null });
      return { success: true, error: null };
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Failed to update profile';
      setState(prev => ({ ...prev, loading: false, error }));
      return { success: false, error };
    }
  }, []);

  const updateManagerProfile = useCallback(async (formData: ManagerProfileFormData) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const profile = await profileApi.updateMe(formData);
      setState({ profile, loading: false, error: null });
      return { success: true, error: null };
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Failed to update profile';
      setState(prev => ({ ...prev, loading: false, error }));
      return { success: false, error };
    }
  }, []);

  const uploadPhoto = useCallback(async (file: File) => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      await profileApi.uploadPhoto(file);
      // Refetch — backend may have just created the profile row
      const profile = await profileApi.getMe();
      setState({ profile, loading: false, error: null });
      return { success: true, error: null };
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Failed to upload photo';
      setState(prev => ({ ...prev, loading: false, error }));
      return { success: false, error };
    }
  }, []);

  const deletePhoto = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      await profileApi.deletePhoto();
      setState(prev => ({
        ...prev,
        profile: prev.profile ? { ...prev.profile, photo_url: undefined } : null,
        loading: false,
      }));
      return { success: true, error: null };
    } catch (err) {
      const error = err instanceof Error ? err.message : 'Failed to delete photo';
      setState(prev => ({ ...prev, loading: false, error }));
      return { success: false, error };
    }
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  return {
    ...state,
    fetchProfile,
    updateProfile,
    updateManagerProfile,
    uploadPhoto,
    deletePhoto,
    clearError,
  };
};
